/**
 * ModBench.tsx — the STARFORGE HUB (route /mods; will become an in-place menu screen).
 *
 * Two tabs:
 *   OPEN — crack Starforge cases (reveal an individual mod)
 *   GEAR — your gun on top + your backpack of individual mods below. Click a mod to
 *          equip it (one per type), or salvage/upgrade it. One mod = one slot, max 50.
 */
import { useState, type CSSProperties } from 'react';
import {
  WEAPON_MODS, WEAPON_MOD_SETS, modComboImage, modLabel, modBuffValue,
  MOD_RARITY_NAME, type ModCode, type ModRarity,
} from '../game/weaponMods';
import { getWeaponDef } from '../game/weapons';
import { getEquipped, equippedCodes, isEquipped, equip, unequip, clearEquipped, type EquippedMod } from '../game/weaponModLoadout';
import {
  getInv, openCase, slotsUsed, MAX_SLOTS,
  salvageItem, upgradeItem, nextRarity, SALVAGE_VALUE, UPGRADE_COST, type ModItem,
} from '../game/starforgeInventory';

const MOD_COLOR: Record<ModCode, string> = {
  D: '#ff5a3c', S: '#33d6ff', C: '#b15cff', P: '#1de0c0', K: '#ffae3c', M: '#39ff14',
};
const RARITY_COLOR: Record<ModRarity, string> = { common: '#39c46b', rare: '#9a7bff', legendary: '#ffce4d' };
const CASE_IMG = '/items/starforge_case.png';
const WEAPONS = Object.keys(WEAPON_MOD_SETS);

interface StatRow { key: string; label: string; color: string; base: number; total: number; max: number; dec: number; unit: string; }
/** Real weapon base stats + equipped-mod buffs → bar-graph rows. */
function statRows(weaponId: string, equipped: EquippedMod[]): StatRow[] {
  const def = getWeaponDef(weaponId);
  const baseDmg = def?.baseDamage ?? 0;
  const baseReload = def?.baseReloadTime ?? 1;
  const baseCrit = (def?.baseCritChance ?? 0) * 100;
  const baseKb = def?.baseKnockback ?? 0;
  const baseProj = def?.baseProjectileCount ?? 1;
  const basePierce = def?.projectile?.piercing ?? 0;
  let dmgPct = 0, frPct = 0, critPct = 0, pierceFlat = 0, kbPct = 0, msFlat = 0;
  for (const e of equipped) {
    const v = modBuffValue(WEAPON_MODS[e.code], e.rarity);
    switch (WEAPON_MODS[e.code].stat) {
      case 'damage': dmgPct += v; break;
      case 'attackSpeed': frPct += v; break;
      case 'crit': critPct += v; break;
      case 'pierce': pierceFlat += v; break;
      case 'knockback': kbPct += v; break;
      case 'multishot': msFlat += v; break;
    }
  }
  const frBase = 1 / baseReload;
  return [
    { key: 'D', label: 'Damage', color: MOD_COLOR.D, base: baseDmg, total: baseDmg * (1 + dmgPct / 100), max: 120, dec: 0, unit: '' },
    { key: 'S', label: 'Fire rate', color: MOD_COLOR.S, base: frBase, total: frBase * (1 + frPct / 100), max: 3, dec: 2, unit: '/s' },
    { key: 'C', label: 'Crit', color: MOD_COLOR.C, base: baseCrit, total: baseCrit + critPct, max: 60, dec: 0, unit: '%' },
    { key: 'P', label: 'Pierce', color: MOD_COLOR.P, base: basePierce, total: basePierce + pierceFlat, max: 8, dec: 0, unit: '' },
    { key: 'K', label: 'Knockback', color: MOD_COLOR.K, base: baseKb, total: baseKb * (1 + kbPct / 100), max: 10, dec: 0, unit: '' },
    { key: 'M', label: 'Projectiles', color: MOD_COLOR.M, base: baseProj, total: baseProj + msFlat, max: 8, dec: 0, unit: '' },
  ];
}

type Tab = 'open' | 'gear';
type Reveal = { code: ModCode | null; rarity: ModRarity; scrap: number; full: boolean };

export default function ModBench() {
  const [tab, setTab] = useState<Tab>('open');
  const [, setTick] = useState(0);
  const rerender = () => setTick(x => x + 1);
  const inv = getInv();

  const weaponId = WEAPONS[0] ?? 'revolver';
  const [equipped, setEquipped] = useState<EquippedMod[]>(() => getEquipped(weaponId));
  const [selId, setSelId] = useState<string | null>(null);
  const [reveal, setReveal] = useState<Reveal | null>(null);

  const codes = equipped.map(e => e.code);
  const gunSrc = modComboImage(weaponId, codes) ?? WEAPON_MOD_SETS[weaponId]?.baseUrl ?? '';
  const slots = WEAPON_MOD_SETS[weaponId]?.slots ?? 6;

  const refreshGun = () => setEquipped(getEquipped(weaponId));

  const doOpen = (i: number) => { const r = openCase(i); setReveal({ code: r.item?.code ?? null, rarity: r.rarity, scrap: r.scrapGained, full: r.full }); rerender(); };

  const onEquip = (it: ModItem) => {
    if (isEquipped(weaponId, it.id)) { setEquipped(unequip(weaponId, it.id)); }
    else { setEquipped(equip(weaponId, { code: it.code, itemId: it.id, rarity: it.rarity })); }
  };
  const onSalvage = (it: ModItem) => { if (isEquipped(weaponId, it.id)) unequip(weaponId, it.id); salvageItem(it.id); setSelId(null); refreshGun(); rerender(); };
  const onUpgrade = (it: ModItem) => {
    if (upgradeItem(it.id) && isEquipped(weaponId, it.id)) equip(weaponId, { code: it.code, itemId: it.id, rarity: it.rarity });
    refreshGun(); rerender();
  };

  const sel = inv.mods.find(m => m.id === selId) ?? null;

  const TABS: { key: Tab; label: string }[] = [
    { key: 'open', label: `📦 Open  (${inv.cases.length})` },
    { key: 'gear', label: `🔧 Gear` },
  ];

  return (
    <div style={st.page}>
      <style>{KEYFRAMES}</style>

      <header style={st.header}>
        <a href="/" style={st.back}>← Menu</a>
        <h1 style={st.title}>JUNK DRAWER</h1>
        <div style={st.counters}>
          <span style={st.pill}>🔧 {inv.scrap} <span style={st.pillSub}>scrap</span></span>
          <span style={st.pill}>📦 {inv.cases.length} <span style={st.pillSub}>cases</span></span>
        </div>
      </header>

      <nav style={st.tabs}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ ...st.tab, ...(tab === t.key ? st.tabOn : {}) }}>{t.label}</button>
        ))}
      </nav>

      {tab === 'open' && (
        <div style={st.body}>
          {inv.cases.length === 0
            ? <div style={st.empty}>No cases to open. Extract from a raid with loot to earn more.</div>
            : <div style={st.caseGrid}>
              {inv.cases.map((rar, i) => (
                <button key={i} onClick={() => doOpen(i)} style={{ ...st.caseCard, borderColor: RARITY_COLOR[rar], boxShadow: `0 0 22px ${RARITY_COLOR[rar]}44` }}>
                  <img src={CASE_IMG} alt="Starforge case" style={st.caseImg} />
                  <div style={{ ...st.caseRar, color: RARITY_COLOR[rar] }}>{rar.toUpperCase()}</div>
                  <div style={st.caseHint}>click to crack open</div>
                </button>
              ))}
            </div>}
        </div>
      )}

      {tab === 'gear' && (
        <div style={st.body}>
          {/* GUN + equipped */}
          <div style={st.gearTop}>
            <div style={st.gunWrap}>
              <div style={{ ...st.gunGlow, boxShadow: `0 0 ${40 + codes.length * 26}px ${10 + codes.length * 8}px rgba(120,200,255,0.5)` }} />
              <img key={gunSrc} src={gunSrc} alt="weapon" style={st.gun} />
            </div>
            <div style={st.benchInfo}>
              <div style={st.benchTitle}>Revolver <span style={st.slotPill}>{equipped.length}/{slots}</span></div>
              {equipped.length === 0
                ? <div style={st.statEmpty}>No mods on it. Tap a mod below to bolt it on.</div>
                : equipped.map(e => (
                  <div key={e.itemId} style={st.statLine}>
                    <span style={{ ...st.statDot, background: MOD_COLOR[e.code] }} />
                    <span style={st.statName}>{WEAPON_MODS[e.code].name}</span>
                    <span style={{ ...st.statRar, color: RARITY_COLOR[e.rarity] }}>{e.rarity[0].toUpperCase()}</span>
                    <span style={st.statVal}>{modLabel(WEAPON_MODS[e.code], e.rarity)}</span>
                    <button style={st.unBtn} title="unequip" onClick={() => { setEquipped(unequip(weaponId, e.itemId)); }}>✕</button>
                  </div>
                ))}
              {equipped.length > 0 && <button style={st.clearBtn} onClick={() => { clearEquipped(weaponId); setEquipped([]); }}>Strip all</button>}
            </div>
          </div>

          {/* STAT BARS — base gun stats, updating live as mods are equipped */}
          <div style={st.statsPanel}>
            {statRows(weaponId, equipped).map(r => {
              const bp = Math.min(100, (r.base / r.max) * 100);
              const tp = Math.min(100, (r.total / r.max) * 100);
              const delta = r.total - r.base;
              return (
                <div key={r.key} style={st.sRow}>
                  <span style={st.sLabel}>{r.label}</span>
                  <div style={st.sTrack}>
                    <div style={{ ...st.sBase, width: `${bp}%`, background: r.color + '55' }} />
                    <div style={{ ...st.sBoost, left: `${bp}%`, width: `${Math.max(0, tp - bp)}%`, background: r.color }} />
                  </div>
                  <span style={st.sVal}>{r.total.toFixed(r.dec)}{r.unit}{delta > 0.001 ? <span style={{ color: r.color, fontWeight: 700 }}> +{delta.toFixed(r.dec)}</span> : null}</span>
                </div>
              );
            })}
          </div>

          {/* BACKPACK — individual mod squares */}
          <div style={st.bpBar}>
            <span style={st.bpTitle}>Backpack</span>
            <span style={st.bpSlots}>{slotsUsed()} / {MAX_SLOTS} slots</span>
          </div>
          <div style={st.itemGrid}>
            {inv.mods.length === 0 && <div style={st.empty}>Backpack empty — open some cases.</div>}
            {inv.mods.map(it => {
              const m = WEAPON_MODS[it.code]; const rc = RARITY_COLOR[it.rarity]; const on = isEquipped(weaponId, it.id); const isSel = selId === it.id;
              return (
                <button key={it.id} onClick={() => setSelId(isSel ? null : it.id)}
                  style={{ ...st.itemSq, borderColor: rc, ...(isSel ? { boxShadow: `0 0 0 2px ${rc}, 0 0 16px ${rc}99` } : {}), ...(on ? { background: '#15243a' } : {}) }}>
                  {on && <span style={st.eqBadge}>ON GUN</span>}
                  <span style={{ ...st.itemRarTag, background: rc }}>{it.rarity[0].toUpperCase()}</span>
                  <img src={m.iconUrl} alt={m.name} style={st.itemIcon} />
                  <div style={st.itemName}>{m.name}</div>
                  <div style={{ ...st.itemBuff, color: rc }}>{modLabel(m, it.rarity)}</div>
                </button>
              );
            })}
          </div>

          {/* action bar for the selected mod */}
          {sel && (() => {
            const m = WEAPON_MODS[sel.code]; const nxt = nextRarity(sel.rarity); const cost = UPGRADE_COST[sel.rarity]; const on = isEquipped(weaponId, sel.id);
            return (
              <div style={st.actionBar}>
                <img src={m.iconUrl} alt="" style={st.abIcon} />
                <div style={st.abMain}>
                  <div style={st.abName}>{m.name} <span style={{ color: RARITY_COLOR[sel.rarity] }}>({sel.rarity})</span></div>
                  <div style={st.abBuff}>{modLabel(m, sel.rarity)}</div>
                </div>
                <button style={{ ...st.eqBtn, ...(on ? st.eqBtnOn : {}) }} onClick={() => onEquip(sel)}>{on ? 'Unequip' : '＋ Equip'}</button>
                {nxt && cost !== undefined && <button style={{ ...st.upBtn, ...(inv.scrap < cost ? st.btnOff : {}) }} disabled={inv.scrap < cost} onClick={() => onUpgrade(sel)}>⬆ {MOD_RARITY_NAME[nxt]} · {cost}🔧</button>}
                <button style={st.salvBtn} onClick={() => onSalvage(sel)}>♻ +{SALVAGE_VALUE[sel.rarity]}🔧</button>
              </div>
            );
          })()}
        </div>
      )}

      {reveal && (
        <div style={st.revealBg} onClick={() => setReveal(null)}>
          <div style={st.revealCard} onClick={e => e.stopPropagation()}>
            <div style={{ ...st.revealRar, color: RARITY_COLOR[reveal.rarity] }}>{reveal.rarity.toUpperCase()} STARFORGE</div>
            {reveal.full || !reveal.code ? (
              <div style={st.revealFull}>Backpack full! Salvaged straight into scrap.</div>
            ) : (
              <>
                <div style={st.revealStage}>
                  <img src={CASE_IMG} alt="" style={{ ...st.revealCase, animation: 'sfShake 0.5s ease' }} />
                  <div style={{ ...st.revealBurst, animation: 'sfBurst 0.6s ease forwards', background: `radial-gradient(circle, ${RARITY_COLOR[reveal.rarity]}cc, transparent 70%)` }} />
                  <img src={WEAPON_MODS[reveal.code].iconUrl} alt="" style={{ ...st.revealMod, animation: 'sfPop 0.5s 0.35s ease both' }} />
                </div>
                <div style={{ ...st.revealName, color: MOD_COLOR[reveal.code] }}>{WEAPON_MODS[reveal.code].name} <span style={{ color: RARITY_COLOR[reveal.rarity], fontSize: 14 }}>({reveal.rarity})</span></div>
                <div style={st.revealBuff}>{modLabel(WEAPON_MODS[reveal.code], reveal.rarity)}</div>
              </>
            )}
            <div style={st.revealScrap}>+{reveal.scrap} 🔧 scrap</div>
            <button style={st.collectBtn} onClick={() => setReveal(null)}>Collect → Backpack</button>
          </div>
        </div>
      )}
    </div>
  );
}

const KEYFRAMES = `
@keyframes sfShake { 0%,100%{transform:translateX(0) rotate(0)} 20%{transform:translateX(-6px) rotate(-3deg)} 40%{transform:translateX(6px) rotate(3deg)} 60%{transform:translateX(-4px) rotate(-2deg)} 80%{transform:translateX(4px) rotate(2deg)} }
@keyframes sfBurst { 0%{opacity:0;transform:scale(0.2)} 50%{opacity:1} 100%{opacity:0;transform:scale(2.2)} }
@keyframes sfPop { 0%{opacity:0;transform:scale(0.2) translateY(10px)} 70%{transform:scale(1.15)} 100%{opacity:1;transform:scale(1) translateY(0)} }
`;

const st: Record<string, CSSProperties> = {
  page: { minHeight: '100vh', background: 'radial-gradient(1200px 600px at 50% -10%, #16243a 0%, #0a0e16 60%)', color: '#e7edf6', fontFamily: 'system-ui, sans-serif', padding: '16px 20px 60px', boxSizing: 'border-box' },
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 },
  back: { color: '#7fb0ff', textDecoration: 'none', fontSize: 15 },
  title: { fontSize: 24, fontWeight: 800, letterSpacing: 3, margin: 0, textShadow: '0 0 18px rgba(120,200,255,0.5)' },
  counters: { marginLeft: 'auto', display: 'flex', gap: 10 },
  pill: { background: '#14202f', border: '1px solid #2c4a6e', borderRadius: 20, padding: '6px 14px', fontSize: 15, fontWeight: 700, color: '#ffe1a8' },
  pillSub: { fontSize: 11, fontWeight: 400, color: '#90a4bd' },
  tabs: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' },
  tab: { background: '#131a26', border: '1px solid #283a52', color: '#9fb3cc', borderRadius: 9, padding: '9px 18px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14 },
  tabOn: { background: '#1d2c42', borderColor: '#5aa0ff', color: '#fff', fontWeight: 700 },
  body: { maxWidth: 1040, margin: '0 auto' },
  empty: { fontSize: 15, color: '#7c8aa0', padding: '30px 0', textAlign: 'center', gridColumn: '1 / -1' },
  caseGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 14 },
  caseCard: { background: '#10161f', border: '2px solid #2a3a52', borderRadius: 14, padding: '14px 10px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#e7edf6', fontFamily: 'inherit' },
  caseImg: { width: 120, height: 120, objectFit: 'contain' },
  caseRar: { fontSize: 13, fontWeight: 800, letterSpacing: 1 },
  caseHint: { fontSize: 11, color: '#76849a' },
  gearTop: { display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 18 },
  gunWrap: { position: 'relative', width: 'min(540px,82vw)', height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  gunGlow: { position: 'absolute', width: '66%', height: 100, borderRadius: '50%', filter: 'blur(8px)', transition: 'all 0.25s ease' },
  gun: { position: 'relative', maxWidth: '100%', maxHeight: 230, objectFit: 'contain', filter: 'drop-shadow(0 10px 22px rgba(0,0,0,0.6))' },
  benchInfo: { minWidth: 260, background: 'rgba(15,22,34,0.7)', border: '1px solid #22354f', borderRadius: 14, padding: '14px 16px' },
  benchTitle: { fontSize: 17, fontWeight: 700, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 },
  slotPill: { fontSize: 12, background: '#14202f', border: '1px solid #2c4a6e', borderRadius: 12, padding: '3px 10px', color: '#bcd4f5' },
  statEmpty: { fontSize: 13, color: '#6f7d92', fontStyle: 'italic' },
  statLine: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' },
  statDot: { width: 9, height: 9, borderRadius: '50%' },
  statName: { fontSize: 13 },
  statRar: { fontSize: 11, fontWeight: 800, border: '1px solid currentColor', borderRadius: 5, padding: '0 5px' },
  statVal: { marginLeft: 'auto', fontSize: 13, fontWeight: 700 },
  unBtn: { background: 'none', border: 'none', color: '#ff8d8d', cursor: 'pointer', fontSize: 13, padding: '0 2px' },
  clearBtn: { marginTop: 10, background: '#2a1417', border: '1px solid #6e2c34', color: '#ffb0b0', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12 },
  statsPanel: { background: 'rgba(15,22,34,0.6)', border: '1px solid #22354f', borderRadius: 12, padding: '12px 16px', marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 9 },
  sRow: { display: 'flex', alignItems: 'center', gap: 12 },
  sLabel: { fontSize: 12.5, color: '#aeb9cb', width: 92, flex: '0 0 auto' },
  sTrack: { position: 'relative', flex: 1, height: 6, background: '#1b2536', borderRadius: 4, overflow: 'hidden' },
  sBase: { position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: 4 },
  sBoost: { position: 'absolute', top: 0, bottom: 0, borderRadius: 4, transition: 'width 0.2s ease, left 0.2s ease' },
  sVal: { fontSize: 12.5, color: '#e7edf6', width: 96, flex: '0 0 auto', textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  bpBar: { display: 'flex', alignItems: 'baseline', gap: 12, borderTop: '1px solid #1d2838', paddingTop: 14, marginBottom: 12 },
  bpTitle: { fontSize: 16, fontWeight: 700 },
  bpSlots: { fontSize: 13, color: '#90a4bd' },
  itemGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: 10 },
  itemSq: { position: 'relative', background: '#10161f', border: '2px solid #233247', borderRadius: 12, padding: '10px 6px 8px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: '#e7edf6', fontFamily: 'inherit' },
  eqBadge: { position: 'absolute', top: 5, left: 5, fontSize: 8.5, fontWeight: 800, letterSpacing: 0.4, color: '#06121f', background: '#5ad1ff', borderRadius: 5, padding: '1px 5px' },
  itemRarTag: { position: 'absolute', top: 5, right: 5, width: 16, height: 16, borderRadius: 4, color: '#06121f', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  itemIcon: { width: 58, height: 58, objectFit: 'contain', marginTop: 6 },
  itemName: { fontSize: 12, fontWeight: 700 },
  itemBuff: { fontSize: 11.5, fontWeight: 600 },
  actionBar: { position: 'sticky', bottom: 10, marginTop: 16, display: 'flex', alignItems: 'center', gap: 12, background: '#0f1826', border: '1px solid #2c4368', borderRadius: 12, padding: '10px 14px', boxShadow: '0 8px 30px rgba(0,0,0,0.5)', flexWrap: 'wrap' },
  abIcon: { width: 44, height: 44, objectFit: 'contain' },
  abMain: { flex: 1, minWidth: 120 },
  abName: { fontSize: 14, fontWeight: 700 },
  abBuff: { fontSize: 12, color: '#cdd9ea' },
  eqBtn: { background: '#173a22', border: '1px solid #39c46b', color: '#cdffd9', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 13 },
  eqBtnOn: { background: '#3a2417', borderColor: '#c08a3a', color: '#ffe0b0' },
  upBtn: { background: '#14233a', border: '1px solid #3a6ea5', color: '#bcdcff', borderRadius: 8, padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 },
  btnOff: { opacity: 0.4, cursor: 'not-allowed' },
  salvBtn: { background: '#241a10', border: '1px solid #6a5326', color: '#ffd98c', borderRadius: 8, padding: '8px 13px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, fontWeight: 600 },
  revealBg: { position: 'fixed', inset: 0, background: 'rgba(4,8,14,0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  revealCard: { background: '#0f1622', border: '1px solid #2a3a52', borderRadius: 18, padding: '24px 30px', textAlign: 'center', width: 350, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' },
  revealRar: { fontSize: 13, fontWeight: 800, letterSpacing: 2, marginBottom: 10 },
  revealFull: { fontSize: 16, color: '#dbe6f5', padding: '24px 0' },
  revealStage: { position: 'relative', height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  revealCase: { position: 'absolute', width: 150, height: 150, objectFit: 'contain', opacity: 0.55 },
  revealBurst: { position: 'absolute', width: 200, height: 200, borderRadius: '50%' },
  revealMod: { position: 'relative', width: 110, height: 110, objectFit: 'contain', filter: 'drop-shadow(0 0 16px rgba(255,255,255,0.3))' },
  revealName: { fontSize: 20, fontWeight: 800, marginTop: 6 },
  revealBuff: { fontSize: 15, color: '#dbe6f5', marginTop: 2 },
  revealScrap: { fontSize: 13, color: '#ffd56b', marginTop: 8 },
  collectBtn: { marginTop: 18, background: '#1f5d22', border: '1px solid #39ff14', color: '#eaffe6', borderRadius: 9, padding: '10px 22px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 14 },
};
